import { Component, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { firstValueFrom, takeUntil } from 'rxjs';
import { DestroyableComponent } from '../../../shared/components/destroyable/destroyable.component';
import { RoomsService } from '../../../core/services/rooms.service';
import { Room } from '../../../core/models/entities/room';
import { fadeAnimation } from '../../../core/animations/fade.animation';
import { Vendor } from '../../../core/models/entities/vendor';
import { RoomAmenity } from '../../../core/models/entities/room-amenity';
import { ToastService } from '../../../core/services/toast.service';
import { Title } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import { fromStore } from 'angular/app/core/store';
import { CancelConditionsHelper } from 'angular/app/core/helpers/cancel-conditions.helper';
import { RoomDetailsModalComponent } from '../../../bookings/components/room-details-modal/room-details-modal.component';
import { ModalService } from 'angular/app/core/services/modal.service';
import { BookableBy } from 'angular/app/core/models/common/bookable-by';
import { NoParticularLayoutId } from 'angular/app/core/models/entities/layout';
import { ApiError } from '../../../core/models/common/api-error';
import { DatetimeHelper } from '../../../core/helpers/datetime.helper';
import { differenceInMinutes, parseISO } from 'date-fns';
import { sortBy, uniq } from 'lodash';

@Component({
  selector: 'app-room-page',
  animations: [fadeAnimation],
  template: `
    <div class="bg-white">
      @if (loading) {
        <app-loader [centered]="true"></app-loader>
      }

      @if (loadingError()) {
        <app-not-found></app-not-found>
      } @else {
        @if (room && !loading) {
          <div>
            <div class="container pt-4 pb-10 lg:pb-16">
              <app-breadcrumb
                [links]="[
                  {
                    title: 'Rechercher une salle',
                    routerLink: '/louer-salle/' + room.vendor.slug,
                  },
                  { title: room.name },
                ]"
              ></app-breadcrumb>
              <div class="mt-10 md:mt-16">
                <app-room-details
                  [room]="room"
                  [vendor]="vendor"
                  [bookingButtonVisible]="bookingButtonVisible"
                  [onRoomClick]="openRoomDetailsModal"
                ></app-room-details>
              </div>
              <!-- ------------------------------------------------------------------------------>
              <!-- ------------------------------------------------------------------------------>
              <!-- ------------------------------------------------------------------------------>
              <div class="mt-20 grid gap-8 md:grid-cols-2 md:gap-20">
                <div class="space-y-4">
                  <h1 class="dashboard">Tarifs de location</h1>
                  @for (rate of ratesGroupedByPrice; track rate.id) {
                    @if (rate.groups.length > 1) {
                      <div class="space-y-5 rounded-lg bg-gray-50 p-4">
                        @if (ratesGroupedByPrice.length > 1) {
                          <div class="text-lg font-medium">{{ rate.name }}</div>
                        }
                        @for (group of rate.groups; track group.days) {
                          <div class="space-y-2">
                            <div class="font-medium">{{ group.days }}</div>
                            <div class="space-y-1">
                              <div class="flex justify-between">
                                <span class="whitespace-nowrap"
                                  >À la journée</span
                                >
                                <span class="text-right whitespace-nowrap">
                                  @if (group.amount_per_day > 0) {
                                    {{ group.amount_per_day | chf }}
                                  } @else {
                                    Gratuit
                                  }
                                </span>
                              </div>
                              @if (room.hourly_rate_enabled) {
                                @if (room.uses_time_slots) {
                                  @for (
                                    time_slot of group.amount_per_time_slot;
                                    track time_slot.duration
                                  ) {
                                    <div class="flex justify-between">
                                      <span
                                        >Le créneau de
                                        {{ time_slot.duration }}</span
                                      >
                                      <span class="text-right">
                                        @if (time_slot.amount > 0) {
                                          {{ time_slot.amount | chf }}
                                        } @else {
                                          Gratuit
                                        }
                                      </span>
                                    </div>
                                  }
                                } @else {
                                  <div class="flex justify-between">
                                    <span>À l'heure</span>
                                    <span class="text-right">
                                      @if (group.amount_per_hour > 0) {
                                        {{ group.amount_per_hour | chf }}
                                      } @else {
                                        Gratuit
                                      }
                                    </span>
                                  </div>
                                }
                              }
                            </div>
                          </div>
                        }
                      </div>
                    } @else {
                      <div
                        class="flex flex-col justify-between gap-2 rounded-lg bg-gray-50 p-4"
                      >
                        @if (ratesGroupedByPrice.length > 1) {
                          <div class="font-medium">{{ rate.name }}</div>
                        }
                        <div class="space-y-1">
                          <div class="flex justify-between gap-6">
                            <span class="whitespace-nowrap">À la journée</span>
                            <span class="text-right whitespace-nowrap">
                              @if (rate.groups[0].amount_per_day > 0) {
                                {{ rate.groups[0].amount_per_day | chf }}
                              } @else {
                                Gratuit
                              }
                            </span>
                          </div>
                          @if (room.hourly_rate_enabled) {
                            @if (room.uses_time_slots) {
                              @for (
                                time_slot of rate.groups[0]
                                  .amount_per_time_slot;
                                track time_slot.duration
                              ) {
                                <div class="flex justify-between">
                                  <span
                                    >Le créneau de
                                    {{ time_slot.duration }}</span
                                  >
                                  <span class="text-right">
                                    @if (time_slot.amount > 0) {
                                      {{ time_slot.amount | chf }}
                                    } @else {
                                      Gratuit
                                    }
                                  </span>
                                </div>
                              }
                            } @else {
                              <div class="flex justify-between">
                                <span>À l'heure</span>
                                <span class="text-right">
                                  @if (rate.groups[0].amount_per_hour) {
                                    {{ rate.groups[0].amount_per_hour | chf }}
                                  } @else {
                                    Gratuit
                                  }
                                </span>
                              </div>
                            }
                          }
                        </div>
                      </div>
                    }
                  }
                </div>
                <div>
                  <h1 class="dashboard">Conditions d'annulation</h1>
                  <div class="pt-8">
                    @for (
                      summaryLine of cancelConditionsSummaryLines;
                      track summaryLine
                    ) {
                      <div class="mb-1">{{ summaryLine }} </div>
                    }
                  </div>
                  <div class="mt-4 inline-block">
                    @if (vendor.grace_period_hours) {
                      <app-alert>
                        <div class="xl:hidden">
                          Après avoir effectué la réservation, vous bénéficiez
                          d’une
                          <span class="font-medium"
                            >période de {{ vendor.grace_period_hours }} heures
                            pour annuler gratuitement</span
                          >.
                        </div>
                        <div class="hidden xl:block">
                          <div>
                            Après avoir effectué la réservation, vous bénéficiez
                            d’une
                          </div>
                          <div class="font-medium"
                            >période de {{ vendor.grace_period_hours }} heures
                            pour annuler gratuitement.
                          </div>
                        </div>
                      </app-alert>
                    }
                  </div>
                </div>
                @if (room.room_amenities?.length) {
                  <div>
                    <h1 class="dashboard">Équipement</h1>
                    <div class="pt-2">
                      <app-room-view-amenities
                        [roomAmenities]="room.room_amenities"
                      ></app-room-view-amenities>
                    </div>
                  </div>
                }
                @if (room.documents?.length) {
                  <div>
                    <h1 class="dashboard">Documentation</h1>
                    <app-room-view-documents
                      [documents]="room.documents"
                    ></app-room-view-documents>
                  </div>
                }
              </div>
            </div>
            <!-- ------------------------------------------------------------------------------>
            <!-- ------------------------------------------------------------------------------>
            <!-- ------------------------------------------------------------------------------>
            <div class="md:mt-20">
              <div class="container">
                <h1 class="dashboard md:text-center"
                  >Capacité selon la disposition</h1
                >
                @if (!noParticularLayout) {
                  <div class="mt-4 md:mt-6 md:text-center"
                    >Le mobilier est à disposition du client et la disposition
                    de la salle sera effectuée par ses soins.
                  </div>
                }
              </div>
              <div class="sm:ml-6 lg:container lg:mx-auto">
                <app-room-view-layouts
                  [layouts]="room.layouts"
                ></app-room-view-layouts>
              </div>
            </div>
            <div class="container md:mt-32">
              <div class="bg-secondary-500 mt-10 h-px"></div>
              <div class="pt-10 md:pt-32">
                <h1 class="dashboard md:text-center">Contacts de référence</h1>
                <div
                  class="mt-8 flex flex-col justify-center space-y-10 md:flex-row md:space-y-0 md:space-x-32"
                >
                  <div>
                    <app-room-view-contact
                      [room]="room"
                      type="admin"
                    ></app-room-view-contact>
                  </div>
                  @if (room.technical_email || room.technical_phone) {
                    <div>
                      <app-room-view-contact
                        [room]="room"
                        type="technical"
                      ></app-room-view-contact>
                    </div>
                  }
                </div>
              </div>
              <!-- ------------------------------------------------------------------------------>
              <!-- ------------------------------------------------------------------------------>
              <!-- ------------------------------------------------------------------------------>
              @if (bookingButtonVisible) {
                <div class="mt-14 text-center md:mt-20">
                  <app-button [routerLink]="['reserver']">Réserver</app-button>
                </div>
              }
              @if (!bookingButtonVisible && !room.is_bookable_publicly) {
                <div class="mt-14 md:mt-20">
                  <app-info-box variant="info">
                    {{
                      room.is_bookable_publicly_message ||
                        'Pour réserver cette salle, veuillez nous contacter directement.'
                    }}
                  </app-info-box>
                </div>
              }
            </div>
          </div>
        }
      }
    </div>

    <div class="h-20 bg-white"></div>
  `,
  standalone: false,
})
export class RoomPageComponent extends DestroyableComponent implements OnInit {
  room: Room;
  includedAmenities: RoomAmenity[] = [];
  excludedAmenities: RoomAmenity[] = [];
  vendor: Vendor;
  loading: boolean;
  bookingButtonVisible: boolean;
  ratesGroupedByPrice: {
    id: number;
    name: string;
    groups: {
      days: string;
      amount_per_day: number;
      amount_per_hour?: number;
      amount_per_time_slot?: { duration: string; amount: number }[];
    }[];
  }[];
  loadingError = signal<boolean>(false);

  constructor(
    private route: ActivatedRoute,
    private roomsService: RoomsService,
    private toastService: ToastService,
    private title: Title,
    private store: Store,
    private modalService: ModalService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.route.params
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(async (params: Params) => {
        this.loading = true;
        try {
          const response = await firstValueFrom(this.roomsService.findOne(params['id']));
          this.room = response.data;
          if (
            this.room.vendor.slug !== params['vendorSlug'] ||
            this.room.bookable_by === BookableBy.Private ||
            !this.roomsService.canUserAccessRoom(this.room)
          ) {
            this.loadingError.set(true);
          } else {
            this.includedAmenities = this.room.room_amenities.filter(
              (roomAmenity: RoomAmenity) => roomAmenity.included,
            );
            this.excludedAmenities = this.room.room_amenities.filter(
              (roomAmenity: RoomAmenity) => !roomAmenity.included,
            );
            this.vendor = this.room.vendor;
            const user = await firstValueFrom(
              this.store.select(fromStore.selectAuthUser),
            );
            const isInternalUser =
              user && user.vendor.id === this.room.vendor_id;
            this.bookingButtonVisible =
              isInternalUser ||
              (this.vendor.accepts_online_payments &&
                this.room.is_bookable_publicly);
            this.title.setTitle(
              this.room.name + ', ' + this.vendor.name + ' - Location de salles',
            );
            this.setRatesGroupedByPrice();
          }
        } catch (exception) {
          this.loadingError.set(true);
          this.toastService.error((exception as ApiError).message);
        }
        this.loading = false;
      });
  }

  get cancelConditionsSummaryLines() {
    return CancelConditionsHelper.getCancelConditionLines(
      this.room.vendor.cancel_conditions,
    );
  }

  get noParticularLayout(): boolean {
    return (
      this.room.layouts.length === 1 &&
      this.room.layouts[0].id === NoParticularLayoutId
    );
  }

  setRatesGroupedByPrice() {
    const timeSlotsDurationsInMinutes =
      this.getRoomTimeSlotsDurationInMinutes();

    this.ratesGroupedByPrice = this.room.current_prices.reduce(
      (result, price) => {
        // Vérifie si un groupe de tarif similaire existe déjà
        const rateIndex = result.findIndex((rate) => rate.id === price.rate.id);

        if (rateIndex === -1) {
          // Ajoute un nouveau tarif
          result.push({
            id: price.rate.id,
            name: price.rate.name,
            groups: [],
          });
        }

        // Ajoute un groupe à ce tarif
        const rate = result.find((r) => r.id === price.rate.id);
        const groupIndex = rate.groups.findIndex(
          (group) =>
            group.amount_per_day === price.amount_per_day &&
            group.amount_per_hour === price.amount_per_hour,
        );

        const dayDescription = this.room.weekday_pricing_enabled
          ? DatetimeHelper.weekdays[price.weekday]?.day_description ||
            'Tous les jours'
          : 'Tous les jours';

        if (groupIndex === -1) {
          // Ajoute un nouveau groupe
          rate.groups.push({
            days: dayDescription,
            amount_per_day: price.amount_per_day,
            amount_per_hour: price.amount_per_hour,
            amount_per_time_slot: !this.room.uses_time_slots
              ? null
              : timeSlotsDurationsInMinutes.map((minutes: number) => {
                  return {
                    duration: this.formatHours(minutes),
                    amount: (minutes / 60) * price.amount_per_hour,
                  };
                }),
          });
        } else {
          // Ajoute le jour au groupe existant
          rate.groups[groupIndex].days += this.room.weekday_pricing_enabled
            ? ', ' + dayDescription.toLowerCase()
            : '';
        }

        return result;
      },
      [],
    );
  }

  private formatHours(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins < 60 && hours === 0) {
      return `${mins}min`;
    } else {
      return mins === 0 ? `${hours}h` : `${hours}h${mins}`;
    }
  }

  private getRoomTimeSlotsDurationInMinutes() {
    const timeSlotsDurationsInMinutes: number[] = [];
    if (this.room.time_slots) {
      this.room.time_slots.forEach((timeSlot) => {
        timeSlotsDurationsInMinutes.push(
          differenceInMinutes(parseISO(timeSlot.end), parseISO(timeSlot.start)),
        );
      });
    }

    return sortBy(uniq(timeSlotsDurationsInMinutes));
  }

  identify(index, item) {
    return item.id;
  }

  openRoomDetailsModal(room: Room) {
    const modalRef = this.modalService.open(RoomDetailsModalComponent, {
      size: 'screen',
      showActionButtons: false,
      title: room.name,
    });
    const component = modalRef.componentRef
      .instance as RoomDetailsModalComponent;
    component.room = room;
    component.vendor = this.vendor;
  }
}
